import { ApiModule } from '@keenvil/littlefinger';
import ApiConfig from '../apiConfig';
/**
 * Amenity Module endpoints
 */

class AmenitiesModule extends ApiModule {
  findAmenities(query) {
    return this.adapter.query('management/amenities/search', query);
  }

  findAmenityById(id) {
    return this.adapter.query(`management/amenities/${id}`);
  }

  getBookings(filters) {
    return this.adapter.query('management/bookings/search', filters);
  }

  getCategories() {
    return this.adapter.query('management/categories');
  }

  createCategory(category) {
    return this.adapter.save('management/categories', category);
  }

  updateCategory(category) {
    return this.adapter.update(
      `management/categories/${category.id}`,
      category
    );
  }

  deleteCategory(id) {
    return this.adapter.delete(`management/categories/${id}`, false);
  }

  uploadAmenityFile(file) {
    return Promise.resolve(file);
  }

  getBookableSlots(amenityId, dates) {
    return this.adapter.query(`booking/bookings/${amenityId}/slots`, {
      ...dates
    });
  }

  createAmenity(amenity) {
    return this.adapter.save('management/amenities', amenity);
  }

  updateAmenityById(amenity) {
    return this.adapter.update(`management/amenities/${amenity.id}`, amenity);
  }

  deleteAmenityById(id) {
    return this.adapter.delete(`management/amenities/${id}`, false);
  }

  addScheduleToAmenity(amenityId, schedule) {
    return this.adapter.save(
      `management/amenities/${amenityId}/schedules`,
      schedule
    );
  }

  addCreditsToUnit(unitId, credits) {
    return this.adapter.save(`management/units/${unitId}/credits`, credits);
  }

  findBookings(query) {
    return this.adapter.query('management/bookings/search', query);
  }

  getBookingById(id) {
    return this.adapter.query(`management/bookings/${id}`);
  }

  createBooking(booking) {
    return this.adapter.save('booking/bookings', booking);
  }

  // Reserva del admin. amenities-api rechaza las reglas con un 400 y el motivo
  // en el cuerpo ({ code, detail }), pero littlefinger no lee el cuerpo de los
  // 400 y el motivo se perdía. Mismo pedido que adapter.save, leyendo el cuerpo.
  // `override`: reserva forzada (saltea las reglas del residente; el back
  // guarda quién la hizo, el motivo y qué regla se salteó).
  bookAsAdmin(booking, { override = false } = {}) {
    const { adapter } = this;
    const path = override ? 'booking/bookings/override' : 'booking/bookings';
    return adapter.transport
      .post(adapter.getHeaders(true), adapter.buildUrl(path), booking)
      .then(response =>
        response.text().then(text => {
          let body = null;
          try {
            body = text ? JSON.parse(text) : null;
          } catch (parseError) {
            body = { detail: text };
          }
          if (!response.ok) {
            const error = new Error((body && body.detail) || 'Booking failed');
            error.apiErrors = [
              { ...(body || {}), httpStatus: response.status }
            ];
            throw error;
          }
          return body;
        })
      );
  }

  cancelBooking(id, reason) {
    return this.adapter.save(`booking/bookings/${id}/cancel`, { reason });
  }

  getCreditsAssignments(filters) {
    let queryString = Object.keys(filters)
      .map(key => {
        if (Array.isArray(filters[key])) {
          return filters[key]
            .map(value => {
              return key + '[]=' + value;
            })
            .join('&');
        }

        return key + '=' + filters[key];
      })
      .join('&');
    return this.adapter.find(
      `management/units/creditAssignments/search?${queryString}`
    );
  }

  getDefaultersList() {
    return this.adapter.query('management/defaulters');
  }

  removeUnitFromDefaultersList(unitId) {
    return this.adapter.delete(`management/defaulters/${unitId}`, false);
  }

  getUnitBalance(unitId) {
    return this.adapter.query(`booking/units/${unitId}/creditBalance`);
  }

  addUnitToDefaultersList(unitId) {
    return this.adapter.save(`management/defaulters`, { unitId });
  }

  // Unidades habilitadas: con `requiresEnrollment` solo reservan estas. Los POST
  // leen texto: la respuesta puede venir vacía y no se usa.
  getUnitEnrollments(amenityId) {
    return this.adapter.query(`management/amenities/${amenityId}/enrollments`);
  }

  setUnitEnrollment(amenityId, unitId, enrolled) {
    return this.adapter.save(
      `management/amenities/${amenityId}/units/${unitId}/enrollment`,
      { enrolled },
      false
    );
  }

  // Topes por unidad. El POST reemplaza los siete: lo que no va queda sin tope.
  getUnitAmenityLimits(amenityId) {
    return this.adapter.query(`management/amenities/${amenityId}/limits`);
  }

  setUnitAmenityLimitsBulk(amenityId, body) {
    return this.adapter.save(
      `management/amenities/${amenityId}/limits/bulk`,
      body,
      false
    );
  }

  // Qué reservas quedarían sin turno si el cronograma nuevo rige desde esa fecha.
  getScheduleImpact(amenityId, effectiveFrom) {
    return this.adapter.query(
      `management/amenities/${amenityId}/schedules/impact`,
      { effectiveFrom }
    );
  }
}

export default new AmenitiesModule('amenities', ApiConfig);
